import { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { translations } from './translations';
import Header from './components/Header';
import Footer from './components/Footer';
import BookingForm from './components/BookingForm';
import ServicesSection from './components/ServicesSection';
import FleetSection from './components/FleetSection';
import BookingSuccess from './components/BookingSuccess';
import BookingCancel from './components/BookingCancel';
import ErrorBoundary from './components/ErrorBoundary';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';

// Hero background image
const HERO_IMAGE = 'https://images.unsplash.com/photo-1686868245180-d5c984a984f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtb25hY28lMjBsdXh1cnklMjB5YWNodHxlbnwwfHx8fDE3NzU3Mzg3NjV8MA&ixlib=rb-4.1.0&q=85';

const HomePage = ({ lang, setLang, t }) => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Header lang={lang} setLang={setLang} t={t} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Luxury Riviera" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24 lg:pt-32 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <div className="animate-fade-in-up">
              <span className="label-luxury">{t('hero.label')}</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 tracking-tighter leading-none" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('hero.title')} <br />
                <span className="text-[#C5A059]">{t('hero.titleHighlight')}</span>
              </h1>
              <p className="text-white/60 mt-6 text-base lg:text-lg leading-relaxed max-w-lg">
                {t('hero.subtitle')}
              </p>

              {/* Quick stats */}
              <div className="flex items-center gap-8 mt-8">
                <div>
                  <div className="text-2xl text-[#C5A059] font-light">24/7</div>
                  <div className="text-white/40 text-xs uppercase tracking-wider">Service</div>
                </div>
                <div className="w-px h-10 bg-white/10"></div>
                <div>
                  <div className="text-2xl text-[#C5A059] font-light">100%</div>
                  <div className="text-white/40 text-xs uppercase tracking-wider">Électrique</div>
                </div>
                <div className="w-px h-10 bg-white/10"></div>
                <div>
                  <div className="text-2xl text-[#C5A059] font-light">VIP</div>
                  <div className="text-white/40 text-xs uppercase tracking-wider">Service</div>
                </div>
              </div>
            </div>

            {/* Right: Booking Form */}
            <div id="booking" className="animate-fade-in-up animation-delay-200">
              <BookingForm lang={lang} t={t} />
            </div>
          </div>
        </div>

        {/* Scroll indicator - Arrow */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <button 
            onClick={() => {
              const el = document.getElementById('services');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex flex-col items-center gap-2 text-white/50 hover:text-[#C5A059] transition-colors cursor-pointer group"
            aria-label="Scroll down"
          >
            <span className="text-xs uppercase tracking-widest">{lang === 'fr' ? 'Découvrir' : 'Discover'}</span>
            <svg 
              className="w-6 h-6 animate-bounce group-hover:text-[#C5A059]" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        </div>
      </section>

      {/* Services Section */}
      <ServicesSection t={t} />

      {/* Fleet Section */}
      <FleetSection t={t} lang={lang} />

      {/* Footer */}
      <Footer t={t} />
    </div>
  );
};

function App() {
  const [lang, setLang] = useState('fr');

  const t = useCallback((key) => {
    const keys = key.split('.');
    let result = translations[lang];
    for (const k of keys) {
      result = result?.[k];
    }
    return result || key;
  }, [lang]);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage lang={lang} setLang={setLang} t={t} />} />
            <Route path="/booking-success" element={<BookingSuccess lang={lang} t={t} />} />
            <Route path="/booking-cancel" element={<BookingCancel lang={lang} t={t} />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
